import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus(t('fillAllFields'));
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4m90k2t20jieso0q1ig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitStatus(t('messageSent'));
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus(t('messageFailed'));
      }
    } catch (error) {
      setSubmitStatus(t('messageFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-slate-900 via-violet-900/20 to-slate-900">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
            <div>
              <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extralight text-white mb-4 sm:mb-6 leading-tight tracking-[0.05em] px-2">
                {t('contactTitle')} <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">{t('contactTitleHighlight')}</span>
                <br />
                {t('contactTitleSuffix')}
              </h3>
              <p className="text-gray-400 text-sm sm:text-base md:text-lg font-light leading-relaxed px-2">
                {t('contactDescription')}
              </p>
            </div>
            
            <div className="text-4xl sm:text-5xl md:text-6xl font-extralight text-white tracking-[0.1em] sm:tracking-[0.2em] opacity-20">
              DEEPQUEST
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-slate-800/30 to-slate-700/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 backdrop-blur-sm border border-gray-700/30">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder={t('yourName')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border border-gray-600/50 rounded-lg sm:rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-white placeholder-gray-400 focus:border-violet-500 focus:outline-none transition-colors duration-300 font-light"
                />
              </div>
              
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder={t('yourEmail')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border border-gray-600/50 rounded-lg sm:rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-white placeholder-gray-400 focus:border-violet-500 focus:outline-none transition-colors duration-300 font-light"
                />
              </div>
              
              <div>
                <textarea
                  name="message"
                  placeholder={t('yourMessage')}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full bg-transparent border border-gray-600/50 rounded-lg sm:rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base text-white placeholder-gray-400 focus:border-violet-500 focus:outline-none transition-colors duration-300 resize-none font-light"
                ></textarea>
                <div className="text-right text-gray-500 text-xs sm:text-sm mt-2">
                  {formData.message.length}/500
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-3 sm:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base font-light hover:from-violet-500 hover:to-purple-500 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer tracking-wide"
              >
                {isSubmitting ? t('sending') : t('sendMessage')}
              </button>
              
              {submitStatus && (
                <div className={`text-center py-2 text-sm ${submitStatus.includes(t('messageSent')) ? 'text-green-400' : 'text-red-400'}`}>
                  {submitStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
